/**
 * Normalize regulation metadata documents for DOC_TABLE (source panel + share).
 * Prefers new `documents[]` objects; falls back to legacy `attachment_name`.
 */

export const DOC_TABLE_CELL_PLACEHOLDER = '—';

export interface RegulationDocumentRow {
  /** Raw filename for download / Doc Chat (`doc_name` or attachment string). */
  fileName: string;
  /** Document `regulatory_version` trimmed, or empty when absent. */
  version: string;
  /** Per-document `release_date` trimmed, or empty when absent. */
  releaseDate: string;
}

/** `documents[]` entry built from agent citation fields. */
export interface RegulationDocumentEntry {
  doc_name: string;
  regulatory_version?: string;
  release_date?: string;
}

/** Per-document file name keys: canonical schema first, agent citation alias second. */
const DOC_NAME_KEYS: readonly string[] = ['doc_name', 'name'];
/** Per-document version keys: canonical schema first, agent citation alias second. */
const DOC_VERSION_KEYS: readonly string[] = ['regulatory_version', 'version'];
/** Per-document release date key, shared by both schemas. */
const DOC_RELEASE_DATE_KEY = 'release_date';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readNested(item: unknown, ...path: string[]): unknown {
  let current: unknown = item;
  for (const key of path) {
    const record = asRecord(current);
    if (!record || !(key in record)) {
      return undefined;
    }
    current = record[key];
  }
  return current;
}

function trimString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** First non-empty trimmed value among `keys`, or empty string. */
function firstString(doc: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const value = trimString(doc[key]);
    if (value) {
      return value;
    }
  }
  return '';
}

function parseAttachmentNames(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
      .map((name) => name.trim());
  }
  if (typeof value === 'string') {
    return value.split('##~').map((name) => name.trim()).filter((name) => name.length > 0);
  }
  return [];
}

function mapDocumentsArray(value: unknown): RegulationDocumentRow[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => toDocumentEntry(entry))
    .filter((entry): entry is RegulationDocumentEntry => entry !== null)
    .map((entry) => ({
      fileName: entry.doc_name,
      version: entry.regulatory_version ?? '',
      releaseDate: entry.release_date ?? '',
    }));
}

function resolveDocumentsValue(item: unknown): unknown {
  const nested = readNested(item, 'metadata', 'documents');
  if (nested !== undefined) {
    return nested;
  }
  return readNested(item, 'documents');
}

function resolveAttachmentValue(item: unknown): unknown {
  const nested = readNested(item, 'metadata', 'attachment_name');
  if (nested !== undefined) {
    return nested;
  }
  return readNested(item, 'attachment_name');
}

/** Maps one document object (either schema) onto a `documents[]` entry. */
function toDocumentEntry(value: unknown): RegulationDocumentEntry | null {
  const doc = asRecord(value);
  const fileName = doc ? firstString(doc, DOC_NAME_KEYS) : '';
  if (!doc || !fileName) {
    return null;
  }
  const entry: RegulationDocumentEntry = { doc_name: fileName };
  const version = firstString(doc, DOC_VERSION_KEYS);
  if (version) {
    entry.regulatory_version = version;
  }
  const releaseDate = trimString(doc[DOC_RELEASE_DATE_KEY]);
  if (releaseDate) {
    entry.release_date = releaseDate;
  }
  return entry;
}

/**
 * Build `documents[]` from agent citation fields: `associated_documents` (objects
 * carrying per-document version / release date, or plain file names) merged with
 * bare file-name sources such as `attachment_names` / `document_name`.
 * Entries with metadata win over bare names for the same file.
 */
export function buildAgentDocumentEntries(
  associatedDocuments: unknown,
  ...fileNameSources: unknown[]
): RegulationDocumentEntry[] {
  const byFileName = new Map<string, RegulationDocumentEntry>();
  const addFileName = (value: unknown): void => {
    const fileName = trimString(value);
    if (fileName && !byFileName.has(fileName)) {
      byFileName.set(fileName, { doc_name: fileName });
    }
  };

  for (const value of Array.isArray(associatedDocuments) ? associatedDocuments : []) {
    const entry = toDocumentEntry(value);
    if (entry) {
      byFileName.set(entry.doc_name, entry);
    } else {
      addFileName(value);
    }
  }
  fileNameSources.forEach((source) => parseAttachmentNames(source).forEach(addFileName));

  return [...byFileName.values()];
}

/**
 * Build document rows for UI. Prefer `documents[]`; else legacy `attachment_name`.
 */
export function getRegulationDocumentRows(item: unknown): RegulationDocumentRow[] {
  const fromDocuments = mapDocumentsArray(resolveDocumentsValue(item));
  if (fromDocuments.length > 0) {
    return fromDocuments;
  }

  return parseAttachmentNames(resolveAttachmentValue(item)).map((fileName) => ({
    fileName,
    version: '',
    releaseDate: '',
  }));
}

/**
 * True when the item has document rows from either schema.
 */
export function hasRegulationDocumentRows(item: unknown): boolean {
  return getRegulationDocumentRows(item).length > 0;
}

/**
 * True when `documents[]` has at least one usable `doc_name` (new schema present).
 */
export function hasNewSchemaDocuments(item: unknown): boolean {
  return mapDocumentsArray(resolveDocumentsValue(item)).length > 0;
}

/**
 * Cell text for Ver. / Released: non-empty value, otherwise placeholder.
 */
const DOC_VERSION_NOT_STATED_PATTERN = /^not\s+stated$/i;

export function formatDocTableCell(
  value: string,
  placeholder: string = DOC_TABLE_CELL_PLACEHOLDER
): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed || DOC_VERSION_NOT_STATED_PATTERN.test(trimmed)) {
    return placeholder;
  }
  return trimmed;
}

/** Already-normalized calendar date: DD-MM-YYYY. */
const DD_MM_YYYY_PATTERN = /^(\d{2})-(\d{2})-(\d{4})$/;

/** ISO calendar date prefix (optional time / Z): YYYY-MM-DD…. */
const ISO_DATE_PREFIX_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Normalize document release dates for display as DD-MM-YYYY.
 * Strips ISO time suffixes (e.g. T00:00:00Z). Leaves empty as placeholder.
 */
export function formatDocReleaseDate(
  value: string,
  placeholder: string = DOC_TABLE_CELL_PLACEHOLDER
): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return placeholder;
  }

  if (DD_MM_YYYY_PATTERN.test(trimmed)) {
    return trimmed;
  }

  const isoMatch = trimmed.match(ISO_DATE_PREFIX_PATTERN);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}-${month}-${year}`;
  }

  return trimmed;
}
